import { create } from 'zustand';

const useChatStore = create((set) => ({
    chats: [],
    messagesList: [],

    // Add a new chat
    addChat: (user) => set((state) => {
        // Check if the chat already exists for this user
        const existingChat = state.chats.find(u => u.userUid === user.userUid);

        // If the chat already exists, just open it
        if (existingChat) {
            const updatedChats = state.chats.map(u =>
                u.userUid === user.userUid ? { ...u, isOpen: true } : u
            );
            return { chats: updatedChats };
        }

        // If chat doesn't exist, add the new chat and make sure it is open
        const updatedChats = [...state.chats, { ...user, isOpen: true, inputValue: '', messages: [] }];

        // Ensure there are no more than 4 chats at a time
        if (updatedChats.length > 3) {
            updatedChats.shift(); // Remove the first (oldest) chat
        }

        return { chats: updatedChats };
    }),

    // Remove a chat
    removeChat: (userId) => set((state) => ({
        chats: state.chats.filter(user => user.userUid !== userId)
    })),

    // Toggle a chat (collapse/expand)
    toggleChat: (userId) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId ? { ...user, isOpen: !user.isOpen } : user
        );
        return { chats: updatedChats };
    }),

    // Update the input value for a user
    updateInput: (userId, value) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId ? { ...user, inputValue: value } : user
        );
        return { chats: updatedChats };
    }),

    // Send a message
    sendMessage: (userId, message) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId
                ? { ...user, messages: [...user.messages, message], inputValue: '' }
                : user
        );
        return { chats: updatedChats };
    }),

    // Store messages if present
    storeMessages: (userId, messages) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userId
                ? { ...user, messages: messages.length > 0 ? messages : user.messages }
                : user
        );
        return { chats: updatedChats };
    }),

    // Set the message ID
    setMessageID: (userId) => set((state) => {
        // Find the corresponding message in messagesList
        const foundMessageListRecord = state.messagesList?.find(
            (message) => message.chatInfo.toUserUid === userId
        );

        // If no message is found, return the state as is
        if (!foundMessageListRecord) return state;

        // Update the msgID in chats where chatInfo.toUserUid matches userId
        const updatedChats = state.chats.map(user =>
            user.userUid === foundMessageListRecord.chatInfo.toUserUid
                ? { ...user, msgID: foundMessageListRecord.chatInfo.msgID }
                : user
        );

        return { chats: updatedChats };
    }),

    setMessagesList: (data) => set((state) => {
        const existingMessages = state.messagesList || [];

        // Merge new data with existing ones while removing duplicates based on chatID & msgID
        const updatedMessagesList = [
            ...existingMessages,
            ...data.filter(
                (newMsg) =>
                    !existingMessages.some(
                        (existingMsg) =>
                            existingMsg.chatID === newMsg.chatID &&
                            existingMsg.chatInfo.msgID === newMsg.chatInfo.msgID
                    )
            ),
        ];

        return { messagesList: updatedMessagesList };
    }),

    setMsgIDDirect: (userUid, msgID) => set((state) => {
        const updatedChats = state.chats.map(user =>
            user.userUid === userUid ? { ...user, msgID: msgID } : user
        );
        return { chats: updatedChats };
    }),

    updateMessages: (userUid, updatedMessages) => set((state) => {
        const updatedChats = state.chats.map(user => {
            if (user.userUid === userUid) {
                // Check if there's already a message with the same chatID
                const messageExists = user.messages.some(message => message.chatID === updatedMessages.chatID);

                // If the message doesn't exist, add it to the messages array
                if (!messageExists) {
                    return {
                        ...user,
                        messages: [...user.messages, updatedMessages]  // Insert updatedMessages object at the end
                    };
                }
                // If the message already exists, return the user object unchanged
                return user;
            }
            return user;
        });
        return { chats: updatedChats };
    }),

    updateChatMessageByID: (message) => set((state) => {
        // Get sender & receiver from local storage
        const receiver = message.receiverUid;
        const sender = message.senderUid;

        // Map through chats and find the chat that contains the message
        const updatedChats = state.chats.map((chat) => {
            // Only update the chat if it contains the message with the matching chatID
            if (chat.messages.some((msg) => msg.chatID === message.chatID)) {
                return {
                    ...chat,
                    messages: chat.messages.map((msg) => {
                        if (msg.chatID === message.chatID) {
                            // Set ownership based on sender/receiver
                            let updatedMessage = { ...msg };

                            if (
                                message.receiverUid === sender &&
                                message.senderUid === receiver &&
                                message.isCompleteDeleted === true
                            ) {
                                updatedMessage.status = "Receive";
                                // updatedMessage.isOwned = false;
                            } else if (
                                message.senderUid === sender &&
                                message.receiverUid === receiver
                            ) {
                                updatedMessage.status = "Send";
                                // updatedMessage.isOwned = true;
                            }

                            // If message status is valid, update the message
                            if (
                                (updatedMessage.status &&
                                    message.senderUid === sender &&
                                    message.receiverUid === receiver) ||
                                (updatedMessage.status &&
                                    message.receiverUid === sender &&
                                    message.senderUid === receiver)
                            ) {
                                return {
                                    ...updatedMessage,
                                    isDeleted: true,
                                    msg:
                                        updatedMessage.status === "Send"
                                            ? message.sndrSideMsg
                                            : message.rcvrSideMsg,
                                    isRead: false,
                                };
                            }
                        }
                        return msg;
                    }),
                };
            }
            return chat;
        });

        return { chats: updatedChats };
    }),

    updateChatReadStatus: (message) => set((state) => {
        if (!message) return state;

        // Find the chat that matches the sender
        const updatedChats = state.chats.map(chat => {
            if (chat.userUid === message.senderUid) {
                // Create a new messages array with updated read status
                const updatedMessages = chat.messages.map(msg =>
                    msg.chatID === message.chatID ? { ...msg, isRead: true } : msg
                );

                // Return updated chat object with modified messages
                return { ...chat, messages: updatedMessages };
            }
            return chat;
        });

        return { chats: updatedChats }; // Ensure state updates properly
    }),


    updateAllChatReadStatus: (messages) => set((state) => {
        const sender = localStorage.getItem("userUid");

        if (!messages || messages.length === 0) return state;

        // Extract unique sender UIDs from the messages
        const senderUids = [...new Set(messages.map(msg => msg.senderUid))];

        // Check if any chat exists for the given sender UIDs
        const userChatExists = state.chats.some(chat => senderUids.includes(chat.userUid));
        if (!userChatExists) return state; // If no matching chat exists, return state.

        // Clone and update chat messages
        const updatedChats = state.chats.map(chat => {
            // Filter messages that belong to this specific chat
            const matchingMessages = messages.filter(msg => msg.receiverUid === sender && msg.senderUid === chat.userUid);

            if (matchingMessages.length > 0) {
                const updatedMessages = chat.messages.map(msg => {
                    // Find if this message exists in the messages array
                    const match = matchingMessages.find(m => m.chatID === msg.chatID);
                    if (match) {
                        return { ...msg, isRead: true }; // Mark message as read
                    }
                    return msg;
                });

                return { ...chat, messages: updatedMessages };
            }
            return chat;
        });

        return { chats: updatedChats };
    }),

    clearAllChats: () => set(() => ({
        chats: []
    })),
}));

export default useChatStore;
